import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useFlow } from 'context/FlowContext';
import { useAuthActions } from 'components/auth/AuthActions';
import { checkGuestOrderStatus, fetchConfirmation } from 'services/publicFlowService';

export default function ConfirmationPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { handleLogin, busy } = useAuthActions();
  const orderId = params.get('order');
  const {
    entryFlow,
    primaryHasFullQuiz,
    customer,
    setCustomer,
    lastOrderId,
    householdMembers,
    addPendingInvite,
    pendingInvites,
    clearCart,
    clearDelivery,
  } = useFlow();
  const [order, setOrder] = useState(null);
  const [inviteEmails, setInviteEmails] = useState({});
  const [inviteSent, setInviteSent] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(orderId ? 'processing' : null);
  const paymentProcessedRef = useRef(false);

  useEffect(() => {
    document.title = 'Order confirmed — Square Fare';
    if (!orderId || paymentProcessedRef.current) return;
    paymentProcessedRef.current = true;

    let cancelled = false;

    const run = async () => {
      // Poll the lightweight status endpoint until the webhook has applied the
      // order (or failed to) — mirrors MealPlanViewer's handlePaymentSuccess.
      let finalStatus = 'timeout';
      const maxAttempts = 30;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelled) return;
        try {
          const result = await checkGuestOrderStatus(orderId);
          if (result.status === 'completed') { finalStatus = 'completed'; break; }
          if (result.status === 'save_failed') { finalStatus = 'save_failed'; break; }
        } catch {
          // ignore polling errors, keep retrying
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (cancelled) return;

      const data = await fetchConfirmation(orderId);
      if (cancelled) return;
      if (data) {
        setOrder(data);
        if (data.customer?.email || data.customer?.name) {
          setCustomer(data.customer);
        }
      }
      // Stripe already charged the card in both 'completed' and 'save_failed' —
      // only a genuinely unresolved/cancelled payment ('timeout') should keep the
      // cart and delivery details around (so the customer doesn't retype them).
      if (finalStatus === 'completed' || finalStatus === 'save_failed') {
        clearCart();
        clearDelivery();
      }
      setPaymentStatus(finalStatus === 'completed' ? 'completed' : finalStatus);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [orderId, setCustomer, clearCart, clearDelivery]);

  const membersNeedingQuiz = householdMembers.filter(
    (m) => !m.hasFullQuiz && m.id !== 'member-self',
  );

  const showOptionalQuiz =
    location.state?.showOptionalQuiz ??
    order?.showOptionalQuiz ??
    (entryFlow === 'menu-first' && !primaryHasFullQuiz);

  const showAccountNextSteps =
    order?.showAccountNextSteps ?? (entryFlow === 'quiz-first' || primaryHasFullQuiz);

  // No client-side estimate — a guessed tax rate/delivery fee could be wrong
  // for this order. If `order` hasn't loaded yet, the total is shown as
  // pending rather than displaying a fabricated number on a receipt.
  const total = order?.total ?? null;

  const sendInvite = (member) => {
    const email = inviteEmails[member.id] || member.email;
    if (!email?.trim()) return;
    addPendingInvite(member.id, email.trim());
    setInviteSent((prev) => ({ ...prev, [member.id]: true }));
  };

  if (paymentStatus === 'processing') {
    return (
      <main className="sf-page sf-confirmation" data-testid="confirmation-processing">
        <div className="sf-page__inner sf-confirmation__inner">
          <div className="sf-confirmation__icon" aria-hidden="true">
            ⏳
          </div>
          <h1 className="sf-page__title">Processing your payment…</h1>
          <p className="sf-page__lead">
            Hang tight — we&apos;re confirming your payment with Stripe. This only takes a few seconds.
          </p>
        </div>
      </main>
    );
  }

  if (paymentStatus === 'save_failed' || paymentStatus === 'timeout') {
    return (
      <main className="sf-page sf-confirmation" data-testid="confirmation-failed">
        <div className="sf-page__inner sf-confirmation__inner">
          <div className="sf-confirmation__icon" aria-hidden="true">
            ⚠️
          </div>
          <h1 className="sf-page__title">Payment received</h1>
          <p className="sf-page__lead">
            {paymentStatus === 'save_failed'
              ? "We collected your payment, but ran into an issue applying your order. Our team has been notified and will follow up shortly."
              : "We collected your payment and are still finishing up your order. Refresh this page in a few minutes, or contact us if it doesn't update."}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="sf-page sf-confirmation" data-testid="confirmation-success">
      <div className="sf-page__inner sf-confirmation__inner">
        <div className="sf-confirmation__icon" aria-hidden="true">
          ✓
        </div>
        <h1 className="sf-page__title">You&apos;re all set!</h1>
        <p className="sf-page__lead">
          Thanks{customer.name ? `, ${customer.name.split(' ')[0]}` : ''}. Your order
          {orderId || lastOrderId ? ` #${(orderId || lastOrderId).slice(-8)}` : ''} is confirmed.
        </p>

        <div className="sf-confirmation__card">
          <p>
            <strong>Total:</strong> {total != null ? `$${Number(total).toFixed(2)}` : 'Finalizing…'}
          </p>
          {order?.delivery?.recipientName && (
            <p>
              <strong>Delivering to:</strong> {order.delivery.recipientName}
            </p>
          )}

          {/* Delivery + driver tracking (Claire, Aug 2026). Replaces the legacy
              site's "you'll receive an email when shipping information is
              available", which promised the wrong channel — tracking goes out by
              text on the delivery day, not by email. */}
          {/* Orders placed before per-meal scheduling carry no deliveryGroups
              and fall back to the single order-level slot. */}
          {order?.deliveryGroups?.length ? (
            <>
              <p>
                <strong>Your food will arrive on:</strong>
              </p>
              <ul className="sf-confirmation__delivery-dates">
                {order.deliveryGroups.map((group) => (
                  <li key={`${group.deliveryDate}-${group.time}`}>
                    {group.label} — {group.mealCount} meal{group.mealCount === 1 ? '' : 's'}
                  </li>
                ))}
              </ul>
            </>
          ) : (order?.delivery?.deliveryDay || order?.delivery?.deliveryTime) ? (
            <p>
              <strong>Your food will arrive on</strong>{' '}
              {[order.delivery.deliveryDay, order.delivery.deliveryTime].filter(Boolean).join(', ')}.
            </p>
          ) : null}
          {order?.confirmationEmailSent === true && (
            <p className="sf-page__meta">
              We&apos;ve emailed a confirmation to {customer.email || 'you'} with your delivery details.
            </p>
          )}
          {order?.confirmationEmailSent === false && (
            <p className="sf-page__meta sf-confirmation__email-warning" role="status">
              ⚠️ Your order is confirmed, but we couldn&apos;t send a confirmation email
              {customer.email ? ` to ${customer.email}` : ''}. Please save your order number
              {orderId || lastOrderId ? ` (#${(orderId || lastOrderId).slice(-8)})` : ''} for your records.
            </p>
          )}
          {(order?.confirmationEmailSent === null || order?.confirmationEmailSent === undefined) && (
            <p className="sf-page__meta">
              We&apos;ll email {customer.email || 'you'} with delivery details.
            </p>
          )}
        </div>

        {membersNeedingQuiz.length > 0 && (
          <section className="sf-confirmation__block sf-confirmation__quiz-nudge">
            <h2>Invite family to complete their quiz</h2>
            <p className="sf-page__lead">
              They can keep simple restrictions for now, or take the quiz for better recommendations next week.
            </p>
            {membersNeedingQuiz.map((member) => (
              <div key={member.id} className="sf-invite-row">
                <strong>Invite {member.name}</strong>
                <div className="sf-invite-row__form">
                  <input
                    className="sf-input"
                    type="email"
                    placeholder={`${member.name}@email.com`}
                    value={inviteEmails[member.id] ?? member.email ?? ''}
                    onChange={(e) =>
                      setInviteEmails((prev) => ({ ...prev, [member.id]: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="sf-btn-flow sf-btn-flow--primary"
                    onClick={() => sendInvite(member)}
                    disabled={inviteSent[member.id]}
                  >
                    {inviteSent[member.id] || pendingInvites?.some((i) => i.memberId === member.id)
                      ? 'Invite sent'
                      : 'Send quiz invite'}
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Getting into the account after paying.
            The page said "your account is linked to this order" and then offered
            only "Back to menu" — there was no way in. Checkout creates the Client
            record from the email entered, and sign-in matches on that same email,
            so signing in here connects the two. Signed-in customers get a direct
            link instead. One button — passwordless has no separate sign-up. */}
        {/* By this point the processing / save_failed / timeout states have
            already returned early, so reaching here means the order succeeded.
            Keyed on having an order rather than on paymentStatus === 'completed',
            because the local dev path leaves that null. */}
        {(orderId || lastOrderId) && (
          <section className="sf-confirmation__block">
            <h2>Manage your order</h2>
            {isAuthenticated ? (
              <div className="sf-confirmation__account">
                You can view your meals, delivery details and nutrition profile from Account any time.
              </div>
            ) : (
              <div className="sf-confirmation__account">
                <p className="sf-page__lead sf-confirmation__account-lead">
                  Sign in with the email you used at checkout to see your meals, delivery details and
                  nutrition profile any time.
                </p>
                <button
                  type="button"
                  onClick={handleLogin}
                  disabled={busy}
                  className="sf-btn-flow sf-btn-flow--primary"
                >
                  {busy ? 'Opening…' : 'Sign in to manage this order'}
                </button>
              </div>
            )}
          </section>
        )}

        {showAccountNextSteps && !showOptionalQuiz && (
          <section className="sf-confirmation__block">
            <h2>What&apos;s next</h2>
            <ul className="sf-confirmation__list">
              <li>Switch household tabs on the menu to order for each person.</li>
            </ul>
            <Link to="/this-weeks-menu" className="sf-btn-flow sf-btn-flow--primary">
              Back to menu
            </Link>
          </section>
        )}

        {showOptionalQuiz && membersNeedingQuiz.length === 0 && (
          <section className="sf-confirmation__block sf-confirmation__quiz-nudge">
            <h2>Want better recommendations for future weeks?</h2>
            <p className="sf-page__lead">
              Complete the quiz to personalize your meal profile if you have not done so.
            </p>
            <Link to="/quiz" className="sf-btn-flow sf-btn-flow--primary">
              Take the quiz (optional)
            </Link>
            <Link to="/this-weeks-menu" className="sf-btn-flow sf-btn-flow--ghost">
              Skip for now
            </Link>
          </section>
        )}
      </div>
    </main>
  );
}
